/**
 * Кабинет хозяйства `/farms/:id` на каноне (agroai-6al.34). Хозяйство —
 * ключевая сущность: здесь видно всё о нём, вкладками.
 *
 * «Обзор» — ситуационный: блоки в порядке правил приоритета
 * (`priority.ts`, журнал решений №271): сверху «Сейчас», затем проблемы с
 * объяснением, почему они наверху, потом ключевые показатели и справка.
 * Остальные вкладки: «Агроциклы и работы», «Вода», «Карантин», «Сдача»,
 * «Контуры», «Агроинспекция и техника» (честная заглушка — данных нет).
 *
 * Вкладка живёт в адресе (`?tab=`): ссылка из реестра карантина или из
 * таблицы контуров открывает нужное место сразу.
 */

import { Link } from 'react-router-dom';
import { CalendarRange, ClipboardCheck, Droplets, Grid3x3, LayoutDashboard, ShieldAlert, Wheat, GitCompare } from 'lucide-react';
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import { PageHeader } from '../../ui/PageHeader';
import { Stack } from '../../ui/Card';
import { Tabs, TabPanel, useTabParam, type TabItem } from '../../ui/Tabs';
import { usePageTrail } from '../../shell/ShellContext';
import { primaryMassif } from '../../data-source/api/farmBench';
import { countOf, num, WORDS } from '../../lib/format';
import { FarmOverview } from './FarmOverview';
import { ContoursTab, CyclesTab, farmWaterCycles, HarvestTab, InspectionTab, WaterJournalDisclosure } from './FarmTabs';
import { WateringView } from './WateringView';
import { FarmQuarantineView } from './FarmQuarantineView';
import { VoluntaryBlock } from '../voluntary/VoluntaryBlock';
import { useFarmCabinet } from './useFarmCabinet';
import { isLoading } from '../../ui/AsyncBlock';
import { cycleName } from './model';
import './cabinet.css';

const m = defineMessages({
  farmFallback: { id: 'farm.cabinet.farmFallback', defaultMessage: 'Хозяйство №{id}', description: 'Заголовок страницы хозяйства, пока имя не загружено; {id} — номер хозяйства' },
  levelDistrict: { id: 'farm.cabinet.level.district', defaultMessage: 'Район', description: 'Уровень в хлебных крошках кабинета хозяйства: район (хоким, менеджер)' },
  districtName: { id: 'farm.cabinet.districtName', defaultMessage: 'Бука', description: 'Название района в хлебных крошках (Букинский район); имя собственное' },
  levelMassif: { id: 'farm.cabinet.level.massif', defaultMessage: 'Массив', description: 'Уровень в хлебных крошках кабинета хозяйства: массив' },
  levelFarm: { id: 'farm.cabinet.level.farm', defaultMessage: 'Хозяйство', description: 'Уровень в хлебных крошках кабинета хозяйства: хозяйство' },
  tabOverview: { id: 'farm.cabinet.tab.overview', defaultMessage: 'Обзор', description: 'Вкладка кабинета хозяйства: обзор (текущее состояние); коротко' },
  tabCycles: { id: 'farm.cabinet.tab.cycles', defaultMessage: 'Агроциклы и работы', description: 'Вкладка кабинета хозяйства: агроциклы и работы' },
  tabWater: { id: 'farm.cabinet.tab.water', defaultMessage: 'Вода', description: 'Вкладка кабинета хозяйства: вода и поливы; коротко' },
  tabQuarantine: { id: 'farm.cabinet.tab.quarantine', defaultMessage: 'Карантин', description: 'Вкладка кабинета хозяйства: карантин; коротко' },
  tabHarvest: { id: 'farm.cabinet.tab.harvest', defaultMessage: 'Сдача', description: 'Вкладка кабинета хозяйства: сдача урожая; коротко' },
  tabContours: { id: 'farm.cabinet.tab.contours', defaultMessage: 'Контуры', description: 'Вкладка кабинета хозяйства: контуры (поля) хозяйства; коротко' },
  tabInspection: { id: 'farm.cabinet.tab.inspection', defaultMessage: 'Агроинспекция и техника', description: 'Вкладка кабинета хозяйства: агроинспекция и техника (данных пока нет)' },
  badgeOverdue: { id: 'farm.cabinet.badge.overdue', defaultMessage: '{works} после срока', description: 'Подсказка (aria-label) числа на вкладке «Агроциклы и работы»; {works} — число с единицей: «5 работ на полях»' },
  badgeWater: { id: 'farm.cabinet.badge.waterGap', defaultMessage: '{count, plural, one {# полив} few {# полива} many {# поливов} other {# полива}} без подачи (поле × полив, окно закрыто)', description: 'Подсказка (aria-label) числа на вкладке «Вода»; {count} — число пар «поле × полив» без подачи воды' },
  badgeQuarantine: { id: 'farm.cabinet.badge.quarantine', defaultMessage: 'красных {red}, жёлтых {yellow}', description: 'Подсказка (aria-label) числа на вкладке «Карантин»; {red}/{yellow} — число контуров с красным/жёлтым статусом' },
  badgeContours: { id: 'farm.cabinet.badge.contours', defaultMessage: '{count} по реестру', description: 'Подсказка (aria-label) числа на вкладке «Контуры»; {count} — число контуров хозяйства по реестру' },
  badgeNoData: { id: 'farm.cabinet.badge.noData', defaultMessage: 'нет данных в системе', description: 'Подсказка (aria-label) значка «—» на вкладке «Агроинспекция и техника»' },
  purposeLoading: { id: 'farm.cabinet.purpose.loading', defaultMessage: 'Кабинет хозяйства: текущее состояние, проблемы, земля, агроциклы, вода, карантин, сдача, контуры.', description: 'Подзаголовок страницы хозяйства, пока агроциклы грузятся (хоким, менеджер)' },
  purposeActive: { id: 'farm.cabinet.purpose.active', defaultMessage: 'Идут: {cycles}. Ход работ — по площади работ самого хозяйства (рядом — по числу работ); доли в районе здесь нет.', description: 'Подзаголовок страницы хозяйства; {cycles} — названия идущих агроциклов через запятую («Хлопок 2026, Пшеница 2026»)' },
  purposeOffseason: { id: 'farm.cabinet.purpose.offseason', defaultMessage: 'Межсезонье: ни один агроцикл хозяйства не идёт на дату. Ход работ — по площади работ самого хозяйства.', description: 'Подзаголовок страницы хозяйства в межсезонье (нет идущих агроциклов)' },
  compare: { id: 'farm.cabinet.compare', defaultMessage: 'Сравнить с другим хозяйством', description: 'Кнопка-ссылка в шапке кабинета хозяйства: открыть сравнение двух хозяйств (хоким, менеджер)' },
  tabsLabel: { id: 'farm.cabinet.tabsLabel', defaultMessage: 'Разделы кабинета хозяйства', description: 'aria-label ряда вкладок кабинета хозяйства' },
});

export const FARM_TABS = ['overview', 'cycles', 'water', 'quarantine', 'harvest', 'contours', 'inspection'] as const;
export type FarmTab = (typeof FARM_TABS)[number];

export function FarmCabinet({ farmId, fallbackName }: { farmId: number; fallbackName?: string }) {
  const data = useFarmCabinet(farmId);
  const [tab, setTab] = useTabParam<FarmTab>(FARM_TABS, 'overview');
  const actor = data.actor.data;
  const t = useT();
  const name = actor?.name ?? fallbackName ?? t(m.farmFallback, { id: farmId });
  const massif = actor ? primaryMassif(actor.land_uses) : null;

  usePageTrail([
    { level: t(m.levelDistrict), name: t(m.districtName), to: '/' },
    { level: t(m.levelMassif), name: massif?.name ?? '—', to: massif ? `/massifs/${massif.id}${data.ctx.linkSearch()}` : undefined },
    { level: t(m.levelFarm), name },
  ]);

  const f = data.facts;
  const qBadge = (f.redContours ?? 0) + (f.yellowContours ?? 0);
  const items: TabItem<FarmTab>[] = [
    { id: 'overview', label: t(m.tabOverview), icon: LayoutDashboard },
    { id: 'cycles', label: t(m.tabCycles), icon: CalendarRange, badge: f.overdue ? num(f.overdue) : undefined, tone: 'warning', badgeLabel: f.overdue ? t(m.badgeOverdue, { works: countOf(f.overdue, WORDS.obligation) }) : undefined },
    { id: 'water', label: t(m.tabWater), icon: Droplets, badge: f.waterGapContours ? num(f.waterGapContours) : undefined, tone: 'warning', badgeLabel: f.waterGapContours ? t(m.badgeWater, { count: f.waterGapContours }) : undefined },
    { id: 'quarantine', label: t(m.tabQuarantine), icon: ShieldAlert, badge: qBadge ? num(qBadge) : undefined, tone: f.redContours ? 'critical' : 'warning', badgeLabel: qBadge ? t(m.badgeQuarantine, { red: num(f.redContours ?? 0), yellow: num(f.yellowContours ?? 0) }) : undefined },
    { id: 'harvest', label: t(m.tabHarvest), icon: Wheat },
    { id: 'contours', label: t(m.tabContours), icon: Grid3x3, badge: actor ? num(actor.contours) : undefined, tone: 'quiet', badgeLabel: actor ? t(m.badgeContours, { count: num(actor.contours) }) : undefined },
    { id: 'inspection', label: t(m.tabInspection), icon: ClipboardCheck, badge: '—', tone: 'quiet', badgeLabel: t(m.badgeNoData) },
  ];
  const activeNames = data.active.map(cycleName).join(', ');
  const water = farmWaterCycles(data);

  return (
    <Stack>
      <PageHeader
        level="farm"
        title={name}
        purpose={
          data.cycles.state === 'loading'
            ? t(m.purposeLoading)
            : data.active.length
              ? t(m.purposeActive, { cycles: activeNames })
              : t(m.purposeOffseason)
        }
        actions={
          <Link className="astra-button" to={`/compare${data.ctx.linkSearch({ a: farmId, ...(data.primary ? { cycle: data.primary.cycle } : {}) })}`}>
            <GitCompare size={15} /> {t(m.compare)}
          </Link>
        }
      />
      <div data-guide="tabs">
        <Tabs label={t(m.tabsLabel)} items={items} value={tab} onChange={setTab} idPrefix="farm-tab" />
      </div>
      <TabPanel id="overview" value={tab} idPrefix="farm-tab">
        <FarmOverview data={data} farmId={farmId} farmName={name} openTab={setTab} />
      </TabPanel>
      <TabPanel id="cycles" value={tab} idPrefix="farm-tab">
        <Stack>
          <CyclesTab data={data} farmId={farmId} />
          <VoluntaryBlock scope={{ kind: 'farm', actorId: farmId }} />
        </Stack>
      </TabPanel>
      <TabPanel id="water" value={tab} idPrefix="farm-tab">
        <div data-guide="water">
        <WateringView
          scope={{ kind: 'farm', id: farmId }}
          cycles={water.cycles}
          defaultCycle={water.fallback}
          asOf={data.asOf}
          params={data.ctx.params}
          paramsKey={data.ctx.key}
          linkSearch={data.ctx.linkSearch}
          after={({ cycle, events }) => <WaterJournalDisclosure data={data} farmId={farmId} cycle={cycle} events={events} />}
          cyclesLoading={isLoading(data.cycles)}
        />
        </div>
      </TabPanel>
      <TabPanel id="quarantine" value={tab} idPrefix="farm-tab">
        <FarmQuarantineView data={data} farmId={farmId} />
      </TabPanel>
      <TabPanel id="harvest" value={tab} idPrefix="farm-tab">
        <HarvestTab data={data} farmId={farmId} />
      </TabPanel>
      <TabPanel id="contours" value={tab} idPrefix="farm-tab">
        <ContoursTab data={data} />
      </TabPanel>
      <TabPanel id="inspection" value={tab} idPrefix="farm-tab">
        <InspectionTab />
      </TabPanel>
    </Stack>
  );
}
